import { castOvertimeImagesToUploadImageWidgetFormValues } from '../../components/OvertimeForm/components/OvertimeUploadImageWidget';
import {
  BaseOvertimeEmployee,
  OvertimeCustomEmployee,
  OvertimeDeletedSAPEmployee,
  OvertimeEmployeeType,
  OvertimeFormEmployee,
  OvertimeFormValues,
  OvertimeSAPEmployee,
} from '../../components/OvertimeForm/form';
import { WorkerRole } from '../../globalTypes';
import { formatISOStringToDateString } from '../../utils/date';
import { formatNumber } from '../../utils/number';
import { OvertimeEditDetails } from './graphql/__generated__/OvertimeEditDetails.query';

type OvertimeEmployees = OvertimeEditDetails['overtime']['employees'];

function mapOvertimeEmployeesToLegacyFormValues(
  employees: OvertimeEmployees,
): OvertimeFormEmployee[] {
  return employees.map((employee) => {
    const machinesHours = employee.machinesHours
      ? formatNumber(employee.machinesHours.toString())
      : '';

    const baseEmployeeValues: BaseOvertimeEmployee = {
      hours: employee.hours ? formatNumber(employee.hours.toString()) : '',
      extraHours: employee.extraHours ? formatNumber(employee.extraHours.toString()) : '',
      materialSurcharge: employee.materialSurcharge ? formatNumber(employee.materialSurcharge) : '',
      machinesUsage: machinesHours,
    };

    switch (employee.__typename) {
      case 'OvertimeCustomEmployee':
        return {
          type: OvertimeEmployeeType.CUSTOM,
          fullName: employee.fullName,
          role: employee.role,
          ...baseEmployeeValues,
        };
      case 'OvertimeSAPEmployee':
        return {
          type: OvertimeEmployeeType.CUSTOM,
          fullName: employee.employee.name,
          role: employee.optionalRole ?? WorkerRole.Fa,
          ...baseEmployeeValues,
        };
      case 'OvertimeDeletedSAPEmployee':
      default:
        return {
          type: OvertimeEmployeeType.CUSTOM,
          fullName: '',
          role: WorkerRole.Fa,
          ...baseEmployeeValues,
        };
    }
  });
}

function mapOvertimeEmployeesToFormValues(employees: OvertimeEmployees): OvertimeFormEmployee[] {
  return employees.map<OvertimeFormEmployee>((employee) => {
    const baseEmployeeValues: BaseOvertimeEmployee = {
      hours: employee.hours ? formatNumber(employee.hours.toString()) : '',
      extraHours: employee.extraHours ? formatNumber(employee.extraHours.toString()) : '',
      materialSurcharge: employee.materialSurcharge ?? '',
      machinesUsage: employee.machinesUsage ?? '',
    };

    switch (employee.__typename) {
      case 'OvertimeCustomEmployee':
        return {
          type: OvertimeEmployeeType.CUSTOM,
          fullName: employee.fullName,
          role: employee.role,
          ...baseEmployeeValues,
        } satisfies OvertimeCustomEmployee;
      case 'OvertimeSAPEmployee':
        return {
          type: OvertimeEmployeeType.SAP,
          employeeId: employee.employee.id,
          fullName: employee.employee.name,
          role: employee.optionalRole ?? undefined,
          automaticRole: employee.automaticRole,
          sapRole: employee.employee.role,
          ...baseEmployeeValues,
        } satisfies OvertimeSAPEmployee;
      case 'OvertimeDeletedSAPEmployee':
        return {
          type: OvertimeEmployeeType.DELETED_SAP,
          employeeId: employee.employeeId,
          fullName: '',
          automaticRole: true,
          ...baseEmployeeValues,
        } satisfies OvertimeDeletedSAPEmployee;
      default:
        return {
          type: OvertimeEmployeeType.SAP,
          fullName: '',
          employeeId: '',
          automaticRole: true,
          sapRole: '',
          ...baseEmployeeValues,
        } satisfies OvertimeSAPEmployee;
    }
  });
}

export function mapOvertimeToFormValues({
  overtime,
  hsProjectNumber,
  pdfRedesignEnabled,
  isDraftProject,
}: {
  overtime: OvertimeEditDetails['overtime'];
  hsProjectNumber?: string;
  pdfRedesignEnabled: boolean;
  isDraftProject: boolean;
}): OvertimeFormValues {
  return {
    no: overtime.alias ? parseFloat(overtime.alias).toString(10) : '',
    date: formatISOStringToDateString(overtime.date),
    projectNumber: isDraftProject ? (overtime.draftProjectNumber ?? '') : (hsProjectNumber ?? ''),
    clientName: overtime.clientNameComputed ?? '',
    professions: overtime.professions ?? [],
    clientReference: overtime.clientReference ?? '',
    description: overtime.descriptionComputed ?? '',
    orderDate: formatISOStringToDateString(overtime.orderDate),
    endDate: overtime.endDate ? formatISOStringToDateString(overtime.endDate) : '',
    workName: overtime.workName,
    workDescription: overtime.workDescription,
    images: castOvertimeImagesToUploadImageWidgetFormValues(overtime.images),
    employees: pdfRedesignEnabled
      ? mapOvertimeEmployeesToFormValues(overtime.employees)
      : mapOvertimeEmployeesToLegacyFormValues(overtime.employees),
  };
}
